import React, { useEffect, useState } from 'react';
import {
  loadKeysByMonthBySku,
  exportKeysByMonthBySku,
} from '../../../../actions/reportByMonthActions';
import { Table, SimpleTextFilter, TableColumn, getVisibleColumns } from '../../../common/Table';
import { CommaNumber_NoDecimal, capitalizeFirstLetter } from '../../../../util/formatter';
import Loading from '../../../common/Loading';
import KwPanel from '../../../common/KwPanel';
import { useLocalStorage } from '../../../../hooks/useLocalStorage';
import {
  useTableSettings,
  getTableSettingsDefaults,
} from '../../../../hooks/useTableSettings';
import { useReportYear } from '../../../../hooks/useReportYear';
import YearFormSelect from '../YearFormSelect';
import CompanyFormSelect from '../CompanyFormSelect';
import AutoSelectText from '../../../common/AutoSelectText';
import GameLink from '../../../common/links/GameLink';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { fetchCompanies } from '../../../../reducers/companyReducer';
import { useAppDispatch, useAppSelector } from '../../../../hooks';

export interface KeysByMonthBySkuListProps { tableViewSwitcher?: React.ReactNode }
function KeysByMonthBySkuList({ tableViewSwitcher }: KeysByMonthBySkuListProps) {
  const companies = useAppSelector((state) => state.companies);
  const currentUser = useAppSelector((state) => state.auth);
  const dispatch = useAppDispatch();

  const location = useLocation();
  const { t } = useTranslation();
  const { year, years, makeHandleYearChange } = useReportYear();
  const months = [
    'jan',
    'feb',
    'mar',
    'apr',
    'may',
    'jun',
    'jul',
    'aug',
    'sep',
    'oct',
    'nov',
    'dec',
  ];
  const allColumns = [
    'game',
    'packageId',
    'steamRef',
    'packageTypeName',
    'keyProviderName',
    'platformName',
    'year',
  ];
  for (const month of months) {
    allColumns.push(month);
  }
  const defaultVisibleColumns = allColumns.filter(
    (x) =>
      x !== 'steamRef' &&
      x !== 'packageTypeName' &&
      x !== 'keyProviderName' &&
      x !== 'platformName'
  );
  const [reload, setReload] = useState<Date | undefined>();
  const [items, setItems] = useState<any>();
  const [company, setCompany] = useLocalStorage<string | null>('report-company', null);
  const params = new URLSearchParams(location.search);
  const {
    tableSettings,
    handleSetTableSettings: setTableSettings,
    filters,
    filterChange,
    isLoading,
    parameters,
  } = useTableSettings(
    'keysbymonthskureport-settings',
    getTableSettingsDefaults({
      defaultSort: 'game',
      defaultVisibleColumns,
    }),
    { year, company },
    loadKeysByMonthBySku,
    setItems,
    reload,
    t('Failed to load report')
  );

  useEffect(() => {
    if (!companies) {
      dispatch(fetchCompanies());
    }
  }, [companies, dispatch]);

  // The base config of the columns, this will be filtered based on the visible (above)
  let columnsConfig: TableColumn[] = [
    {
      Key: 'game',
      Label: t('Game'),
      width: '20%',
      Render: (item) => (
        <GameLink
          gameId={item.gameId}
          gameTitle={item.game}
          skuId={item.skuId}
          skuName={item.sku}
          currentUser={currentUser}
        />
      ),
      Filter: {
        Render: (col) => (
          <SimpleTextFilter
            Key={col.Key}
            Value={col.filterValue ?? params.get(`f_${col.Key}`) ?? ''}
            onChange={(value) => filterChange(col.Key, value)}
          />
        ),
      },
    },
    {
      Key: 'steamRef',
      Label: t('Product code'),
      className: 'autoselect',
      width: '20%',
      Render: (item) =>
        item.steamRef ? <AutoSelectText>{item.steamRef}</AutoSelectText> : '-',
    },
    {
      Key: 'packageId',
      Label: t('Package Id'),
      className: 'autoselect text-center',
      width: '10%',
      Render: (item) =>
        item.packageId ? (
          <AutoSelectText>{item.packageId}</AutoSelectText>
        ) : (
          '-'
        ),
    },
    {
      Key: 'packageType',
      Label: t('Package type'),
      Render: (item) => item.packageType,
    },
    {
      Key: 'keyProvider',
      Label: t('Key provider'),
      Render: (item) => item.keyProvider,
    },
    {
      Key: 'platform',
      Label: t('Platform'),
      Render: (item) => item.platform,
    },
    {
      Key: 'year',
      Label: t('Year'),
      Render: (item) => item.year,
    },
  ];
  // Add the months to the array
  for (const month of months) {
    columnsConfig.push({
      Key: month,
      Label: t(capitalizeFirstLetter(month)),
      className: 'text-right num no-wrap',
      Render: (row) => CommaNumber_NoDecimal(row[month]),
    });
  }

  // filter
  const visibleColumns = tableSettings.columns ?? defaultVisibleColumns;
  const columns = getVisibleColumns(visibleColumns, columnsConfig);
  const handleYearChange = makeHandleYearChange(parameters, setReload);

  const handleCompanyChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setCompany(e.target.value);
    parameters.current = { ...parameters.current, company: e.target.value };
    setReload(new Date());
  };

  // return an alaska table
  return items ? (
    <>
      <div className="form-horizontal row px-4 px-md-0">
        <div className="col-md-6">
          <YearFormSelect
            year={year}
            years={years}
            handleYearChange={handleYearChange}
          ></YearFormSelect>
        </div>
        <div className="col-md-6">
          <CompanyFormSelect
            company={company}
            companies={companies}
            handleCompanyChange={handleCompanyChange}
          ></CompanyFormSelect>
        </div>
      </div>
      <KwPanel className="mb-4">
        <Table
          className="table-v-stripes"
          columns={columns}
          allColumns={allColumns}
          defaultVisibleColumns={defaultVisibleColumns}
          rows={items.data.map((x: any) => {
            return { Key: x.skuId, ...x };
          })}
          totalRowCount={items.totalRowCount}
          filteredRowCount={items.filteredRowCount}
          tableSettings={tableSettings}
          setTableSettings={setTableSettings}
          isLoading={isLoading}
          filters={filters}
          filterChange={filterChange}
          exportData={(filters, scopes) =>
            exportKeysByMonthBySku(year, company, filters, scopes)
          }
          tableActionBarButtons={tableViewSwitcher}
        />
      </KwPanel>
    </>
  ) : (
    <Loading></Loading>
  );
}

export default KeysByMonthBySkuList;
